import type { MergeDropdownCheckRun } from '@/components/top-navigation/merge-dropdown/check-section/types';
import type { CommitCheckSuites } from '@/types/github';
import { AlertCircle, Check, CircleDashed, Loader2, XIcon } from 'lucide-react';
import pluralize from 'pluralize';

import { cn } from '@/lib/utils';

export function getCheckIcon(
  checkRun:
    | CommitCheckSuites[number]
    | {
        status: MergeDropdownCheckRun['status'] | null | undefined;
        conclusion: MergeDropdownCheckRun['conclusion'] | undefined;
      },
  className?: string,
) {
  if (checkRun.status === 'in_progress') {
    return <Loader2 className={cn('size-4 animate-spin text-muted-foreground', className)} />;
  }

  if (checkRun.status === 'queued') {
    return <CircleDashed className={cn('size-4 text-muted-foreground', className)} />;
  }

  if (checkRun.status === 'completed') {
    switch (checkRun.conclusion) {
      case 'success':
        return <Check className={cn('size-4 text-muted-foreground', className)} />;
      case 'failure':
        return <XIcon className={cn('size-4 text-red-500', className)} />;
      case 'neutral':
        return <CircleDashed className={cn('size-4 text-muted-foreground', className)} />;
      case 'cancelled':
      case 'timed_out':
        return <XIcon className={cn('size-4 text-yellow-500', className)} />;
      case 'action_required':
        return <AlertCircle className={cn('size-4 text-yellow-500', className)} />;
      case 'skipped':
        return <CircleDashed className={cn('size-4 text-muted-foreground', className)} />;
      default:
        return <CircleDashed className={cn('size-4 text-muted-foreground', className)} />;
    }
  }

  return <CircleDashed className={cn('size-4 text-muted-foreground', className)} />;
}

export const getApprovalStatus = (
  numApprovals: number,
  numRequiredApprovals: number,
  numReviewers: number,
) => {
  const numMissingApprovals = numRequiredApprovals - numApprovals;

  if (numMissingApprovals > 0) {
    return {
      type: 'missing_approvals' as const,
      numMissingApprovals,
      message:
        numApprovals === 0
          ? `Requires ${numRequiredApprovals} ${pluralize('approval', numRequiredApprovals)}`
          : `Requires ${numMissingApprovals} more ${pluralize('approval', numMissingApprovals)}`,
    };
  }

  if (numApprovals > 0) {
    if (numApprovals === numReviewers) {
      return {
        type: 'has_approvals' as const,
        message: `${numApprovals} ${pluralize('approval', numApprovals)}`,
      };
    }

    return {
      type: 'has_approvals' as const,
      message: `${numApprovals} ${pluralize('approval', numApprovals)} from ${numReviewers} ${pluralize('reviewer', numReviewers)}`,
    };
  }

  return {
    type: 'no_reviews' as const,
    message: 'No reviews yet',
  };
};
